import { memo } from 'react';
import PersonAddAlt1SharpIcon from '@mui/icons-material/PersonAddAlt1Sharp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, Menu, MenuItem, Divider, Typography, Box, Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import DirectionsBusFilledIcon from '@mui/icons-material/DirectionsBusFilled';

import ImageSearchSharpIcon from '@mui/icons-material/ImageSearchSharp';

import PropTypes from 'prop-types';
import useTransactionAssignementActions from 'hooks/transaction/useTransactionAssignementActions';
import useUnassignedLaneAssignDelay from 'hooks/transaction/videoWall/useUnassignedLaneAssignDelay';
import { VIDEO_WALL_UNASSIGNED_ASSIGN_DELAY_MS } from 'store/transactionsSlice/transactionConstants';
import styles from './TransactionAssignmentActions.module.css';
import LinearLoader from 'components/UI/Loaders/LinearLoader';

const UNASSIGNED_WAIT_TOOLTIP = `You can assign it in ${VIDEO_WALL_UNASSIGNED_ASSIGN_DELAY_MS / 1000} seconds if auto-assignment can't find a free agent.`;

// Pass unassignedSince only for an Unassigned lane. The delay lives here rather than in the tile so only a
// mounted button runs its timer and only the button re-renders when it ends.
const TransactionAssignmentActions = ({ cameraId, hasTransaction, assignee, assignOnClick, className = '', mode, unassignedSince = null }) => {
    const isAssignDelayElapsed = useUnassignedLaneAssignDelay(unassignedSince);
    const disabledTooltip = isAssignDelayElapsed ? '' : UNASSIGNED_WAIT_TOOLTIP;
    const {
        anchorEl,
        open,
        handleClick,
        handleClose,
        handlePreview,
        handleAddToQueue,
        handleMissedDetection,
        isTransactionAssignmentDisabled: isDisabled,
    } = useTransactionAssignementActions({
        cameraId,
        cameraHasTransaction: hasTransaction,
        assignee,
        assignOnClick,
    });

    const isSplitViewTransactionMode = mode === 'splitViewTransaction';
    const isTabViewTransactionMode = mode === 'tabViewTransaction';

    const dropDownIcon = !assignOnClick ? <ArrowDropDownIcon sx={{ p: 0, ml: 0 }} /> : null;

    const renderTabViewButton = (disabled = false) => {
        return (
            <Tooltip title="If you need to process a double trailer or a second passenger, click to open another transaction" placement="top">
                <span>
                    <Button
                        id="transaction-assignment-button"
                        aria-controls={open ? 'transaction-assignment-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                        variant="text"
                        color="inherit"
                        size="small"
                        disabled={disabled}
                        sx={{
                            minWidth: 0,
                            px: '10px',
                            color: 'rgba(0, 0, 0, 0.72)',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            '&:hover': { border: '1px solid rgba(0, 0, 0, 0.2)', backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                        }}
                    >
                        <PersonAddAlt1SharpIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>
        );
    };

    const renderButton = () => {
        if (isDisabled) {
            if (isTabViewTransactionMode) {
                return renderTabViewButton(true);
            }
            if (mode) return <LinearLoader containerStyles={{ position: 'relative', width: '150px', alignItems: 'flex-end' }} />;
            return <CircularProgress size={16} />;
        }

        if (isSplitViewTransactionMode) {
            return (
                <Tooltip title="If you need to process a double trailer or a second passenger, click to open another transaction">
                    <button
                        id="transaction-assignment-button"
                        aria-controls={open ? 'transaction-assignment-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                        disabled={isDisabled}
                        className={styles.splitViewBtn}
                    >
                        <PersonAddAlt1SharpIcon sx={{ fontSize: '13px' }} />
                        Assign another
                    </button>
                </Tooltip>
            );
        }

        const buttonCaption = `Assign${isTabViewTransactionMode ? ' new' : ''}`;

        if (isTabViewTransactionMode) return renderTabViewButton();

        // An Unassigned lane has no transaction yet, so the "another transaction" hint does not apply. An empty title
        // also closes the wait tooltip when the delay ends under the cursor, instead of swapping in that hint.
        const enabledTooltip =
            unassignedSince == null ? 'If you need to process a double trailer or a second passenger, click to open another transaction' : '';

        // Remount the tooltip when the lane stops waiting (e.g. auto-assigned under the cursor). Kept mounted, it stays
        // open with the hint above, and with the hover listener now off nothing ever closes it.
        return (
            <Tooltip
                key={unassignedSince == null ? 'lane' : 'unassigned'}
                title={disabledTooltip || enabledTooltip}
                disableHoverListener={!disabledTooltip && !mode}
            >
                <span>
                    <Button
                        id="transaction-assignment-button"
                        aria-controls={open ? 'transaction-assignment-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={isDisabled || !!disabledTooltip}
                        startIcon={<PersonAddAlt1SharpIcon />}
                        endIcon={dropDownIcon}
                        sx={{
                            height: '1.1rem',
                            width: 'max-content',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            pr: dropDownIcon ? '3px' : '10px',
                            '& .MuiButton-endIcon': {
                                m: 0,
                                p: 0,
                            },
                        }}
                    >
                        {buttonCaption}
                    </Button>
                </span>
            </Tooltip>
        );
    };

    return (
        <>
            <div className={className}>{renderButton()}</div>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                MenuListProps={{
                    id: 'transaction-assignment-menu',
                    'aria-labelledby': 'transaction-assignment-button',
                }}
                sx={{
                    '& .MuiPaper-root': {
                        width: '264px',
                        borderRadius: '4px',
                    },
                    '& .MuiList-root': {
                        p: '8px 0',
                    },
                }}
            >
                <MenuItem disabled sx={{ opacity: 1, cursor: 'default', '&.Mui-disabled': { opacity: 1 } }}>
                    <Typography
                        sx={{
                            fontSize: '10px',
                            color: 'rgba(0, 0, 0, 0.6)',
                            fontWeight: 400,
                            letterSpacing: '-0.23px',
                        }}
                    >
                        Reason for manual assign (can change later)
                    </Typography>
                </MenuItem>

                <MenuItem
                    onClick={handleMissedDetection}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '4px 16px',
                        gap: '4px',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                >
                    <Box>
                        <DirectionsBusFilledIcon sx={{ fontFamily: 'Material Icons', fontSize: '24px', color: 'rgba(0, 0, 0, 0.56)' }} />
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: 'rgba(0, 0, 0, 0.87)',
                                    lineHeight: 'normal',
                                    letterSpacing: '0.15px',
                                }}
                            >
                                Missed detection
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 400,
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    lineHeight: 'normal',
                                    letterSpacing: '0.17px',
                                }}
                            >
                                AI did not assign it in 3s.
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 400,
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    lineHeight: 'normal',
                                    letterSpacing: '0.17px',
                                }}
                            >
                                Helps improve accuracy.
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: 'rgba(78, 151, 255, 0.12)',
                                padding: '0 2px',
                                borderRadius: '2px',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 400,
                                    color: '#1870eb',
                                    lineHeight: '1.5',
                                    letterSpacing: '0.15px',
                                }}
                            >
                                Default
                            </Typography>
                        </Box>
                    </Box>
                </MenuItem>

                <MenuItem
                    onClick={handlePreview}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '4px 16px',
                        gap: '4px',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                >
                    <Box sx={{ minWidth: '24px', height: '24px', display: 'flex', alignItems: 'center' }}>
                        <ImageSearchSharpIcon sx={{ fontFamily: 'Material Icons', fontSize: '24px', color: 'rgba(0, 0, 0, 0.56)' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 500,
                                color: 'rgba(0, 0, 0, 0.87)',
                                lineHeight: 'normal',
                                letterSpacing: '0.15px',
                            }}
                        >
                            Preview
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '10px',
                                fontWeight: 400,
                                color: 'rgba(0, 0, 0, 0.6)',
                                lineHeight: 'normal',
                                letterSpacing: '0.17px',
                            }}
                        >
                            Check cameras and relays
                        </Typography>
                    </Box>
                </MenuItem>

                <Divider sx={{ margin: '0' }} />

                <MenuItem
                    onClick={handleAddToQueue}
                    sx={{
                        padding: '4px 16px',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: 'rgba(0, 0, 0, 0.87)',
                            lineHeight: '1.5',
                            letterSpacing: '0.15px',
                        }}
                    >
                        Add to queue
                    </Typography>
                </MenuItem>
            </Menu>
        </>
    );
};

TransactionAssignmentActions.propTypes = {
    cameraId: PropTypes.number,
    hasTransaction: PropTypes.bool,
    assignee: PropTypes.string,
    className: PropTypes.string,
    assignOnClick: PropTypes.bool,
    mode: PropTypes.string,
    unassignedSince: PropTypes.number,
};

export default memo(TransactionAssignmentActions);
